import { useState, useEffect, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { api } from '../utils/api';
import { toast } from 'sonner';
import {
  Plus,
  TrendingUp,
  Receipt,
  MailWarning,
  Search,
  CalendarRange
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableHeader, TableRow, TableHead, TableBody, TableCell } from '@/components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Progress } from '@/components/ui/progress';
import LogoLoader from '@/components/LogoLoader';
import { formatDisplayDate } from '@/utils/date';

const formatCurrency = (value) => {
  const amount = Number.isFinite(value) ? Number(value) : 0;
  return `$${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const INVOICE_STATUS_LABELS = {
  draft: 'Draft',
  sent: 'Issued',
  viewed: 'Viewed',
  approved: 'Approved',
  paid: 'Paid',
  overdue: 'Overdue',
  void: 'Void'
};

const ACK_STATUS_DESCRIPTORS = {
  sent: { label: 'Sent', className: 'bg-green-50 text-green-700 border-green-200' },
  failed: { label: 'Failed', className: 'bg-red-50 text-red-700 border-red-200' },
  queued: { label: 'Queued', className: 'bg-blue-50 text-blue-700 border-blue-200' },
  pending: { label: 'Pending', className: 'bg-amber-50 text-amber-700 border-amber-200' },
  skipped: { label: 'Not sent', className: 'bg-gray-50 text-gray-700 border-gray-200' },
};

const normalizeReceipt = (receipt) => {
  const rawDate = receipt?.receipt_date ?? '';
  const receiptDate = typeof rawDate === 'string' ? rawDate.split('T')[0] : '';
  const allocations = Array.isArray(receipt?.allocations)
    ? receipt.allocations.map((allocation) => ({
        invoice_id: allocation.invoice_id,
        amount: Number(Number(allocation.amount ?? 0).toFixed(2)),
      }))
    : [];

  return {
    id: receipt?.receipt_id ?? receipt?.id ?? '',
    receipt_id: receipt?.receipt_id ?? receipt?.id ?? '',
    client_id: receipt?.client_id ?? '',
    receipt_date: receiptDate,
    currency: receipt?.currency ?? 'USD',
    amount_received: Number(Number(receipt?.amount_received ?? 0).toFixed(2)),
    payment_method: receipt?.payment_method || 'Manual',
    reference: receipt?.reference || '',
    allocations,
    acknowledgement_status: (receipt?.acknowledgement_status || 'skipped').toLowerCase(),
    acknowledgement_email: receipt?.acknowledgement_email || '',
    acknowledgement_communication_id: receipt?.acknowledgement_communication_id || '',
  };
};

const convertInvoiceForReceipts = (invoice, paymentTotals) => {
  const totals = (invoice?.line_items || []).reduce(
    (acc, item) => {
      const quantity = Number(item?.quantity ?? 0);
      const rate = Number(item?.unit_price ?? 0);
      const taxRate = Number(item?.tax_rate ?? 0);
      const lineSubtotal = quantity * rate;
      const lineTax = lineSubtotal * taxRate;
      acc.subtotal += Number.isFinite(lineSubtotal) ? lineSubtotal : 0;
      acc.tax += Number.isFinite(lineTax) ? lineTax : 0;
      return acc;
    },
    { subtotal: 0, tax: 0 }
  );

  const subtotal = Number(totals.subtotal.toFixed(2));
  const taxAmount = Number(totals.tax.toFixed(2));
  const total = Number((subtotal + taxAmount).toFixed(2));
  const paid = Number((paymentTotals.get(invoice.invoice_id) ?? 0).toFixed(2));
  const balance = Number(Math.max(total - paid, 0).toFixed(2));

  const domainStatus = invoice.status;

  return {
    id: invoice.invoice_id,
    client_id: invoice.client_id,
    invoice_no: invoice.invoice_id,
    domainStatus,
    status: INVOICE_STATUS_LABELS[domainStatus] || domainStatus,
    total,
    amount_paid: paid,
    balance
  };
};

const Receipts = () => {
  const { token } = useAuth();
  const [receipts, setReceipts] = useState([]);
  const [clients, setClients] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [showDialog, setShowDialog] = useState(false);
  const [loading, setLoading] = useState(true);
  const [clientFilter, setClientFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [methodFilter, setMethodFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [viewMode, setViewMode] = useState('table');
  const [dateRange, setDateRange] = useState({ start: '', end: '' });
  const [formData, setFormData] = useState({
    client_id: '',
    receipt_date: '',
    amount_received: '',
    payment_method: 'Check',
    reference: '',
    invoice_id: '',
    allocation_amount: ''
  });

  const getClientName = (id) => clients.find(c => c.id === id)?.name || 'Unknown';
  const getInvoiceNumber = (id) => invoices.find(i => i.id === id)?.invoice_no || 'Unknown';

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [receiptsPayload, clientsData, invoicesData] = await Promise.all([
        api.get('/accounting/receipts', token),
        api.get('/clients', token),
        api.get('/accounting/invoices?scope=open', token)
      ]);
      const normalizedReceipts = receiptsPayload
        .map((receipt) => normalizeReceipt(receipt))
        .sort((a, b) => {
          const timeA = Date.parse(a?.receipt_date ?? '');
          const timeB = Date.parse(b?.receipt_date ?? '');
          if (Number.isNaN(timeA) && Number.isNaN(timeB)) return 0;
          if (Number.isNaN(timeA)) return 1;
          if (Number.isNaN(timeB)) return -1;
          return timeB - timeA;
        });
      const paymentTotals = normalizedReceipts.reduce((map, receipt) => {
        (receipt.allocations || []).forEach((allocation) => {
          const current = map.get(allocation.invoice_id) ?? 0;
          map.set(allocation.invoice_id, current + Number(allocation.amount ?? 0));
        });
        return map;
      }, new Map());
      setReceipts(normalizedReceipts);
      setClients(clientsData);
      setInvoices(
        invoicesData.map((invoice) => convertInvoiceForReceipts(invoice, paymentTotals))
      );
    } catch (error) {
      toast.error('Failed to load receipts');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (!formData.invoice_id) {
        toast.error('Please select an invoice to allocate the payment.');
        return;
      }

      const amountReceived = parseFloat(formData.amount_received);
      const allocationAmount = parseFloat(
        formData.allocation_amount || formData.amount_received
      );

      if (!Number.isFinite(amountReceived) || amountReceived <= 0) {
        toast.error('Enter a valid receipt amount.');
        return;
      }

      if (allocationAmount > amountReceived) {
        toast.error('Allocation cannot exceed the amount received.');
        return;
      }

      const receivedAtIso = formData.receipt_date
        ? new Date(`${formData.receipt_date}T00:00:00Z`).toISOString()
        : new Date().toISOString();

      await api.post(
        '/accounting/payments/manual',
        {
          invoice_id: formData.invoice_id,
          amount: allocationAmount,
          method: formData.payment_method || 'Manual',
          received_at: receivedAtIso,
          reference: formData.reference || undefined
        },
        token
      );
      toast.success('Receipt recorded successfully');
      setShowDialog(false);
      loadData();
      setFormData({
        client_id: '',
        receipt_date: '',
        amount_received: '',
        payment_method: 'Check',
        reference: '',
        invoice_id: '',
        allocation_amount: ''
      });
    } catch (error) {
      toast.error('Failed to record receipt');
    }
  };

  const resetFilters = () => {
    setClientFilter('all');
    setStatusFilter('all');
    setMethodFilter('all');
    setSearchTerm('');
    setDateRange({ start: '', end: '' });
  };

  const parseReceiptDate = (dateString) => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split('-');
    const parsed = new Date(Number(year), Number(month) - 1, Number(day));
    return Number.isNaN(parsed.getTime()) ? null : parsed;
  };

  const uniquePaymentMethods = useMemo(() => {
    return Array.from(new Set(receipts.map((receipt) => receipt.payment_method).filter(Boolean)));
  }, [receipts]);

  const acknowledgementSummary = useMemo(() => {
    return receipts.reduce(
      (acc, receipt) => {
        const key = (receipt.acknowledgement_status || 'skipped').toLowerCase();
        acc.counts[key] = (acc.counts[key] ?? 0) + 1;
        acc.total += 1;
        if (key === 'sent') {
          acc.sent += 1;
        }
        if (key === 'failed' || key === 'pending' || key === 'queued') {
          acc.pending += 1;
        }
        return acc;
      },
      { counts: {}, total: 0, sent: 0, pending: 0 }
    );
  }, [receipts]);

  const filteredReceipts = useMemo(() => {
    const startDate = dateRange.start ? parseReceiptDate(dateRange.start) : null;
    const endDate = dateRange.end ? parseReceiptDate(dateRange.end) : null;

    return receipts.filter((receipt) => {
      const statusKey = (receipt.acknowledgement_status || 'skipped').toLowerCase();
      const matchesClient = clientFilter === 'all' || receipt.client_id === clientFilter;
      const matchesStatus = statusFilter === 'all' || statusKey === statusFilter;
      const matchesMethod = methodFilter === 'all' || receipt.payment_method === methodFilter;

      const receiptDate = parseReceiptDate(receipt.receipt_date);
      const matchesStart = !startDate || (receiptDate && receiptDate >= startDate);
      const matchesEnd = !endDate || (receiptDate && receiptDate <= endDate);

      const search = searchTerm.trim().toLowerCase();
      const clientName = clients.find((client) => client.id === receipt.client_id)?.name?.toLowerCase() || '';
      const invoiceNumbers = receipt.allocations
        .map((alloc) => getInvoiceNumber(alloc.invoice_id))
        .join(' ') // fallback string for search
        .toLowerCase();
      const matchesSearch =
        !search ||
        clientName.includes(search) ||
        (receipt.reference || '').toLowerCase().includes(search) ||
        invoiceNumbers.includes(search);

      return matchesClient && matchesStatus && matchesMethod && matchesStart && matchesEnd && matchesSearch;
    });
  }, [receipts, clientFilter, statusFilter, methodFilter, dateRange, searchTerm, clients, invoices]);

  const totals = useMemo(() => {
    const receiptTotals = receipts.reduce(
      (acc, receipt) => {
        const amount = Number(receipt.amount_received || 0);
        const allocated = (receipt.allocations || []).reduce(
          (sum, alloc) => sum + Number(alloc.amount || 0),
          0
        );
        acc.total += amount;
        acc.allocated += allocated;
        return acc;
      },
      { total: 0, allocated: 0 }
    );

    const filteredTotals = filteredReceipts.reduce(
      (acc, receipt) => {
        const amount = Number(receipt.amount_received || 0);
        const allocated = (receipt.allocations || []).reduce(
          (sum, alloc) => sum + Number(alloc.amount || 0),
          0
        );
        acc.total += amount;
        acc.allocated += allocated;
        return acc;
      },
      { total: 0, allocated: 0 }
    );

    const now = new Date();
    const monthToDate = receipts.reduce((sum, receipt) => {
      const date = parseReceiptDate(receipt.receipt_date);
      if (!date) return sum;
      return date.getMonth() === now.getMonth() && date.getFullYear() === now.getFullYear()
        ? sum + Number(receipt.amount_received || 0)
        : sum;
    }, 0);

    return {
      all: receiptTotals,
      filtered: filteredTotals,
      monthToDate
    };
  }, [receipts, filteredReceipts]);

  const averageReceipt = receipts.length ? totals.all.total / receipts.length : 0;
  const deliveryRate = acknowledgementSummary.total
    ? Math.round((acknowledgementSummary.sent / acknowledgementSummary.total) * 100)
    : 0;

  const timelineReceipts = useMemo(() => {
    return [...filteredReceipts].sort((a, b) => {
      const timeA = Date.parse(a.receipt_date || '');
      const timeB = Date.parse(b.receipt_date || '');
      if (Number.isNaN(timeA) && Number.isNaN(timeB)) return 0;
      if (Number.isNaN(timeA)) return 1;
      if (Number.isNaN(timeB)) return -1;
      return timeB - timeA;
    });
  }, [filteredReceipts]);

  if (loading) {
    return <LogoLoader message="Loading receipts..." />;
  }

  return (
    <div className="space-y-6" data-testid="receipts-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Receipts</h1>
          <p className="text-gray-600 mt-1">Record and allocate payments from clients</p>
          <div className="text-sm text-gray-500 mt-1">
            {`${filteredReceipts.length} of ${receipts.length} receipts shown · ${formatCurrency(totals.filtered.total)} filtered total`}
          </div>
        </div>
        <Button onClick={() => setShowDialog(true)} className="bg-utis-forest hover:bg-utis-forest/90 text-white" data-testid="record-receipt-button">
          <Plus className="w-4 h-4 mr-2" />
          Record Receipt
        </Button>
      </div>

      <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Card className="border-utis-sage/60">
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-500">Total Receipts</p>
                <p className="text-3xl font-semibold text-utis-forest mt-2">
                  {formatCurrency(totals.all.total)}
                </p>
                <p className="text-xs text-gray-500 mt-1">Month to date: {formatCurrency(totals.monthToDate)}</p>
              </div>
              <TrendingUp className="w-6 h-6 text-utis-forest" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-500">Allocated to Invoices</p>
                <p className="text-3xl font-semibold text-gray-900 mt-2">
                  {formatCurrency(totals.all.allocated)}
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  {formatCurrency(totals.filtered.allocated)} currently visible
                </p>
              </div>
              <Receipt className="w-6 h-6 text-gray-400" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-500">Pending Acknowledgements</p>
                <p className="text-3xl font-semibold text-amber-600 mt-2">
                  {acknowledgementSummary.pending}
                </p>
                <p className="text-xs text-gray-500 mt-1">{acknowledgementSummary.total - acknowledgementSummary.pending} cleared</p>
              </div>
              <MailWarning className="w-6 h-6 text-amber-600" />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-start justify-between">
              <div>
                <p className="text-sm text-gray-500">Average Receipt</p>
                <p className="text-3xl font-semibold text-gray-900 mt-2">
                  {formatCurrency(averageReceipt)}
                </p>
                <p className="text-xs text-gray-500 mt-1">Across {receipts.length} receipts</p>
              </div>
              <CalendarRange className="w-6 h-6 text-gray-400" />
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid gap-6 lg:grid-cols-[2fr,1fr]">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle>Filters</CardTitle>
            <CardDescription>Combine search, clients, delivery state, and dates to refine the ledger</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-4">
              <div className="space-y-2">
                <Label htmlFor="receipt-search">Search</Label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    id="receipt-search"
                    value={searchTerm}
                    onChange={(event) => setSearchTerm(event.target.value)}
                    placeholder="Client, reference, or invoice"
                    className="pl-9"
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Client</Label>
                <Select value={clientFilter} onValueChange={setClientFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All clients" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All clients</SelectItem>
                    {clients.filter((client) => client.is_active).map((client) => (
                      <SelectItem key={client.id} value={client.id}>
                        {client.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Delivery Status</Label>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All statuses" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All statuses</SelectItem>
                    {Object.entries(ACK_STATUS_DESCRIPTORS).map(([key, descriptor]) => (
                      <SelectItem key={key} value={key}>
                        {descriptor.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Payment Method</Label>
                <Select value={methodFilter} onValueChange={setMethodFilter}>
                  <SelectTrigger>
                    <SelectValue placeholder="All methods" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All methods</SelectItem>
                    {uniquePaymentMethods.map((method) => (
                      <SelectItem key={method} value={method}>
                        {method}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="receipt-start-date">From date</Label>
                <Input
                  id="receipt-start-date"
                  type="date"
                  value={dateRange.start}
                  onChange={(event) => setDateRange((prev) => ({ ...prev, start: event.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="receipt-end-date">To date</Label>
                <Input
                  id="receipt-end-date"
                  type="date"
                  value={dateRange.end}
                  onChange={(event) => setDateRange((prev) => ({ ...prev, end: event.target.value }))}
                />
              </div>
            </div>

            <div className="flex flex-wrap gap-3 items-center justify-between">
              <div className="text-sm text-gray-500">
                Showing {filteredReceipts.length} receipt{filteredReceipts.length === 1 ? '' : 's'} totaling{' '}
                {formatCurrency(totals.filtered.total)}
              </div>
              <Button variant="outline" onClick={resetFilters}>
                Reset filters
              </Button>
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-b from-gray-50 to-white border-gray-200">
          <CardHeader>
            <CardTitle>Delivery health</CardTitle>
            <CardDescription>Monitor acknowledgement performance</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            <div>
              <div className="flex items-center justify-between text-sm text-gray-600">
                <span>Delivered</span>
                <span>{deliveryRate}%</span>
              </div>
              <Progress value={deliveryRate} className="h-2 mt-2" />
            </div>
            <div className="space-y-3">
              {Object.entries(ACK_STATUS_DESCRIPTORS).map(([key, descriptor]) => (
                <div key={key} className="flex items-center justify-between text-sm">
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className={`px-2 py-0.5 rounded-full border ${descriptor.className}`}
                    >
                      {descriptor.label}
                    </Badge>
                  </div>
                  <span className="text-gray-600">{acknowledgementSummary.counts[key] ?? 0}</span>
                </div>
              ))}
            </div>
            <div className="rounded-md border border-dashed border-gray-200 bg-white p-3 text-xs text-gray-500">
              Deliveries marked as failed or pending will trigger reminders for finance follow-up.
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs value={viewMode} onValueChange={setViewMode}>
        <TabsList className="grid w-full grid-cols-2 md:w-auto">
          <TabsTrigger value="table">Ledger view</TabsTrigger>
          <TabsTrigger value="timeline">Timeline</TabsTrigger>
        </TabsList>
        <TabsContent value="table" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Receipt ledger</CardTitle>
              <CardDescription>
                {filteredReceipts.length} receipt{filteredReceipts.length === 1 ? '' : 's'} · {formatCurrency(totals.filtered.total)} total
              </CardDescription>
            </CardHeader>
            <CardContent className="pt-6">
              {filteredReceipts.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No receipts match your filters. Adjust filters or record a new receipt.
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Client</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead>Amount</TableHead>
                      <TableHead>Method</TableHead>
                      <TableHead>Reference</TableHead>
                      <TableHead>Delivery</TableHead>
                      <TableHead>Allocated To</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filteredReceipts.map((receipt) => (
                      <TableRow key={receipt.id}>
                        <TableCell className="font-medium">{getClientName(receipt.client_id)}</TableCell>
                        <TableCell>{formatDisplayDate(receipt.receipt_date, '-')}</TableCell>
                        <TableCell className="font-bold text-utis-forest">{formatCurrency(receipt.amount_received)}</TableCell>
                        <TableCell>
                          <Badge variant="outline">{receipt.payment_method}</Badge>
                        </TableCell>
                        <TableCell>{receipt.reference || '-'}</TableCell>
                        <TableCell>
                          {(() => {
                            const statusKey = (receipt.acknowledgement_status || '').toLowerCase();
                            const descriptor = ACK_STATUS_DESCRIPTORS[statusKey] || ACK_STATUS_DESCRIPTORS.skipped;
                            return (
                              <div className="space-y-1">
                                <Badge
                                  variant="outline"
                                  className={`px-2 py-0.5 rounded-full text-xs font-medium border ${descriptor.className}`}
                                >
                                  {descriptor.label}
                                </Badge>
                                <span className="text-xs text-gray-500">
                                  {receipt.acknowledgement_email ? `Sent to ${receipt.acknowledgement_email}` : 'No recipient'}
                                </span>
                              </div>
                            );
                          })()}
                        </TableCell>
                        <TableCell>
                          <div className="space-y-1">
                            {receipt.allocations.map((alloc, idx) => (
                              <div key={idx} className="text-sm">
                                {getInvoiceNumber(alloc.invoice_id)}: ${alloc.amount.toLocaleString()}
                              </div>
                            ))}
                            {receipt.allocations.length === 0 && <span className="text-gray-400">Unallocated</span>}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
        <TabsContent value="timeline" className="mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Recent receipt timeline</CardTitle>
              <CardDescription>Chronological view of receipt activity and allocations</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {timelineReceipts.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  No receipts available to display on the timeline.
                </div>
              ) : (
                <div className="space-y-6">
                  {timelineReceipts.map((receipt) => {
                    const descriptor = ACK_STATUS_DESCRIPTORS[
                      (receipt.acknowledgement_status || '').toLowerCase()
                    ] || ACK_STATUS_DESCRIPTORS.skipped;
                    return (
                      <div key={receipt.id} className="relative pl-6">
                        <div className="absolute left-0 top-2 h-full w-px bg-gray-200" aria-hidden />
                        <div className="absolute left-0 top-2 -translate-x-1/2">
                          <div className="h-3 w-3 rounded-full bg-utis-forest" />
                        </div>
                        <div className="flex flex-col gap-2">
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <div>
                              <p className="text-sm font-medium text-gray-900">{getClientName(receipt.client_id)}</p>
                              <p className="text-xs text-gray-500">
                                {formatDisplayDate(receipt.receipt_date, '-')} · {receipt.payment_method}
                              </p>
                            </div>
                            <div className="text-lg font-semibold text-utis-forest">
                              {formatCurrency(receipt.amount_received)}
                            </div>
                          </div>
                          <div className="flex flex-wrap items-center gap-2 text-xs text-gray-600">
                            <Badge
                              variant="outline"
                              className={`px-2 py-0.5 rounded-full border ${descriptor.className}`}
                            >
                              {descriptor.label}
                            </Badge>
                            {receipt.reference && <span>Reference: {receipt.reference}</span>}
                            {receipt.acknowledgement_email && <span>Delivered to {receipt.acknowledgement_email}</span>}
                          </div>
                          <div className="rounded-md bg-gray-50 p-3 text-xs text-gray-600 space-y-1">
                            <p className="font-medium text-gray-700">Allocations</p>
                            {receipt.allocations.length === 0 ? (
                              <p>No allocations recorded</p>
                            ) : (
                              receipt.allocations.map((alloc, idx) => (
                                <p key={idx}>
                                  {getInvoiceNumber(alloc.invoice_id)} · ${alloc.amount.toLocaleString(undefined, {
                                    minimumFractionDigits: 2,
                                    maximumFractionDigits: 2
                                  })}
                                </p>
                              ))
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Record Receipt Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Record Receipt</DialogTitle>
            <DialogDescription>Record a payment received from client</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Client *</Label>
              <Select value={formData.client_id} onValueChange={(val) => setFormData({...formData, client_id: val})} required>
                <SelectTrigger>
                  <SelectValue placeholder="Select client" />
                </SelectTrigger>
                <SelectContent>
                  {clients.filter(c => c.is_active).map(client => (
                    <SelectItem key={client.id} value={client.id}>{client.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Receipt Date *</Label>
              <Input type="date" value={formData.receipt_date} onChange={(e) => setFormData({...formData, receipt_date: e.target.value})} required />
            </div>
            <div>
              <Label>Amount Received ($) *</Label>
              <Input type="number" step="0.01" value={formData.amount_received} onChange={(e) => setFormData({...formData, amount_received: e.target.value})} required placeholder="5000.00" />
            </div>
            <div>
              <Label>Payment Method</Label>
              <Select value={formData.payment_method} onValueChange={(val) => setFormData({...formData, payment_method: val})}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Check">Check</SelectItem>
                  <SelectItem value="Wire Transfer">Wire Transfer</SelectItem>
                  <SelectItem value="ACH">ACH</SelectItem>
                  <SelectItem value="Credit Card">Credit Card</SelectItem>
                  <SelectItem value="Cash">Cash</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Reference/Check No</Label>
              <Input value={formData.reference} onChange={(e) => setFormData({...formData, reference: e.target.value})} placeholder="CHK-12345" />
            </div>

            <div className="border-t pt-4">
              <Label className="text-base font-medium">Allocation</Label>
              <p className="text-sm text-gray-600 mb-3">Allocate this receipt to an invoice</p>
              <div>
                <Label>Invoice</Label>
                <Select value={formData.invoice_id} onValueChange={(val) => setFormData({...formData, invoice_id: val})} required>
                  <SelectTrigger>
                    <SelectValue placeholder="Select invoice" />
                  </SelectTrigger>
                  <SelectContent>
                    {invoices.filter(inv => inv.client_id === formData.client_id && inv.balance > 0 && inv.domainStatus !== 'void').map(invoice => (
                      <SelectItem key={invoice.id} value={invoice.id}>
                        {invoice.invoice_no} - {formatCurrency(invoice.total)} (Outstanding: {formatCurrency(invoice.balance)})
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              {formData.invoice_id && (
                <div className="mt-3">
                  <Label>Allocation Amount ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={formData.allocation_amount}
                    onChange={(e) => setFormData({...formData, allocation_amount: e.target.value})}
                    placeholder={formData.amount_received || "Amount to allocate"}
                  />
                </div>
              )}
            </div>

            <Button type="submit" className="w-full bg-utis-forest hover:bg-utis-forest/90 text-white">Record Receipt</Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Receipts;
